import React, { useState } from "react";
import styles from "./BookingTable.module.scss";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
} from "@mui/material";
import { FaEye } from "react-icons/fa";
import BookingDetailsModal from "../../modal/BookingModel/BookingDetailModel";
import * as XLSX from "xlsx";

export interface IBookingTable {
  tableTitle: { id: number; title: string }[];
  tableData: any[];
  loading?: boolean;
}

interface City {
  address: string;
}

const BookingTable = ({ tableTitle, tableData }: IBookingTable) => {
  const [openModel, setOpenModel] = useState(false);
  const [selectedViewBookingData, setSelectedViewBookingData] = useState<any>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const formattedDate = (idx: any) => {
    const date = new Date(idx);
    return date.toLocaleDateString();
  };

  const handleSelectedViewBookingData = (item: any) => {
    setSelectedViewBookingData(item);
    setOpenModel(true);
  };

  const handleCloseModal = () => {
    setOpenModel(false);
    setSelectedViewBookingData(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToExcel = () => {
    const flattenedData = tableData && tableData?.map(item => ({
      "advancePaymentAmount": item.payment.advancePayment.amount,
      "advancePaymentMethod": item.payment.advancePayment.method,
      "pendingAmountAmount": item.payment.pendingAmount.amount,
      "pendingAmountMethod": item.payment.pendingAmount.method,
      
      "Vehicle_Type": item?.quotationDetails?.Vehicle_Type || "N/A",
      "Model": item?.quotationDetails?.Model || "N/A",
      "totalSeat": item.quotationDetails?.totalSeat || "N/A",
      "Rate_Km": item.quotationDetails.Rate_Km,
      "Minimum_Run": item.quotationDetails.Minimum_Run,
      "Driver_Charge": item.quotationDetails.Driver_Charge,
      "Trip_Start_DateTime": item.quotationDetails.Trip_Start_DateTime,
      "Trip_End_DateTime": item.quotationDetails.Trip_End_DateTime,
      "Trip_Days": item.quotationDetails?.Trip_Days || "N/A",
      "Trip_Distance": item?.quotationDetails?.Trip_Distance || "N/A",
      "Manual_Distance": item?.quotationDetails?.Manual_Distance || "N/A",
      "Max_Distance": item?.quotationDetails?.Max_Distance || "N/A",
      "Distance_Rate": item?.quotationDetails?.Distance_Rate || "N/A",
      "Toll_Tax": item?.quotationDetails?.Toll_Tax || "N/A", 
      "Driver_Allowance": item?.quotationDetails?.Driver_Allowance || "N/A",
      "Quotation_without_GST": item?.quotationDetails?.Quotation_without_GST || "N/A",
      "GST": item?.quotationDetails?.GST || "N/A",
      "Net_Payable": item?.quotationDetails?.Net_Payable || "N/A",
      "Booking_Advance": item?.quotationDetails?.Booking_Advance || "N/A",
  
      "firstName": item?.passengerDetails.firstName,
      "middleName": item?.passengerDetails?.middleName || "N/A",
      "lastName": item?.passengerDetails?.lastName || "N/A",
      "mobileNumber": item?.passengerDetails?.mobileNumber || "N/A",
      "alternateNumber": item?.passengerDetails?.alternateNumber || "N/A",
  
      "_id": item._id,
      "userId": item.userId?._id || "N/A",
      "userEmail": item.userId?.email || "N/A",
      "userName": item.userId?.name || "N/A" ,
      "venderId": item.venderId,
      "transactionIds": item.transactionIds.join(", "),
      "source": item.source,
      "destination": item.destination,
      "pickUptime": item.pickUptime,
      "dropTime": item.dropTime,
      "inBetweenCities": (item.inBetweenCities as { address: string }[]).map(city => city.address).join(", "),
      "vendorVehicleId": item.vendorVehicleId?._id || "N/A",
      "vendorVehicleNumber": item.vendorVehicleId?.vehicleNumber || "N/A",
      "rentalVehicleRateId": item?.rentalVehicleRateId || "N/A" ,
      "pickupDate": item?.pickupDate || "N/A",
      "dropoffDate": item?.dropoffDate || "N/A",
      "isSchedule": item?.isSchedule || "N/A",
      "totalPayableAmount": item?.totalPayableAmount || "N/A",
      "roundTrip": item?.roundTrip || "N/A",
      "tnc": item?.tnc || "N/A",
      "extras": item?.extras?.join(", ") || "N/A",
      "tripStatus": item.tripStatus,
      "createdAt": item.createdAt,
      "updatedAt": item.updatedAt
    }));
  
    const ws = XLSX.utils.json_to_sheet(flattenedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    XLSX.writeFile(wb, "Bookings.xlsx");
  };
  

  return (
    <Grid className={styles.bookingTableContainer}>
      <Button variant="contained" color="primary" onClick={handleExportToExcel} sx={{marginBottom:"10px"}}>
        Export to Excel
      </Button>
      <TableContainer className={styles.tableContainer}>
        <Table>
          <TableHead style={{ backgroundColor: "#3FACE2" }}>
            <TableRow>
              {tableTitle.map((item: any) => (
                <TableCell
                  key={item.title}
                  style={{ color: "#000000", textAlign: "left", fontSize: 15, fontWeight: 600 }}
                >
                  {item.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData && tableData?.filter((employee) =>
                query === "" || employee.name?.toLowerCase().includes(query.toLowerCase())
              )
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item: any, idx: any) => (
                <TableRow key={idx}>
                  <TableCell sx={{ textAlign: "left" }}>{item?.passengerDetails?.firstName}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.userId?.email}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>
                    {formattedDate(item?.createdAt)}
                    <FaEye onClick={() => handleSelectedViewBookingData(item)} style={{ cursor: "pointer" }} />
                  </TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.payment?.advancePayment?.amount?.toFixed(2)}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.payment?.advancePayment?.method}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.payment?.pendingAmount?.amount?.toFixed(2)}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.totalPayableAmount?.toFixed(2)}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.source}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.destination}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.destination?.pincode}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{item?.destination?.state}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{new Date(item?.quotationDetails?.Trip_Start_DateTime)?.toLocaleTimeString()}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{new Date(item?.quotationDetails?.Trip_Start_DateTime)?.toLocaleDateString()}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{new Date(item?.quotationDetails?.Trip_End_DateTime)?.toLocaleTimeString()}</TableCell>
                  <TableCell sx={{ textAlign: "left" }}>{new Date(item?.quotationDetails?.Trip_End_DateTime)?.toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={tableData?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <BookingDetailsModal open={openModel} handleClose={handleCloseModal} item={selectedViewBookingData} />
      </TableContainer>
    </Grid>
  );
};

export default BookingTable;
